import { useState, useMemo, useEffect, useContext } from "react";
import { Info, DateTime, Interval } from "luxon";
import classnames from "classnames";
import { TbArrowLeft, TbArrowRight } from "react-icons/tb";
import { GiNotebook } from "react-icons/gi";
import { MdOutlineEdit } from "react-icons/md";
import PropTypes from "prop-types";
import { YearContext } from "../../context/context";

const MobileCalendar = ({ activeDay, setActiveDay, userData }) => {
  const today = DateTime.local();
  const { years } = useContext(YearContext);

  const [currentWeek, setCurrentWeek] = useState(activeDay.startOf("week"));
  const [selectedMonth, setSelectedMonth] = useState(activeDay.month);
  const [selectedYear, setSelectedYear] = useState(activeDay.year);

  const weekdays = useMemo(() => Info.weekdays("short"), []);

  const daysOfWeek = useMemo(() => {
    return Interval.fromDateTimes(currentWeek, currentWeek.plus({ days: 7 }))
      .splitBy({ day: 1 })
      .map((day) => day.start);
  }, [currentWeek]);

  // Check if a day has journal entries
  const hasJournalEntry = (day) => {
    if (!userData?.notes) return false;
    const dayISO = day.toISODate();
    return userData.notes.some(note => 
      note.note_date && DateTime.fromISO(note.note_date).toISODate() === dayISO
    );
  };


  const goToPreviousWeek = () => {
    const newWeek = currentWeek.minus({ week: 1 });
    setCurrentWeek(newWeek);
    setSelectedMonth(newWeek.month);
    setSelectedYear(newWeek.year);
  };
  const goToNextWeek = () => {
    const newWeek = currentWeek.plus({ week: 1 });
    setCurrentWeek(newWeek);
    setSelectedMonth(newWeek.month);
    setSelectedYear(newWeek.year);
  };
  const goToToday = () => {
    setCurrentWeek(today.startOf("week"));
    setActiveDay(today);
    setSelectedMonth(today.month);
    setSelectedYear(today.year);
  };

  const getMonthOptions = () => {
    const months = Info.months("long");
    const availableYears = years && years.length > 0 ? years : [new Date().getFullYear()];
    const options = [];
    for (const year of availableYears) {
      for (let i = 1; i <= 12; i++) {
        options.push({ month: i, year, label: `${months[i - 1]} ${year}` });
      }
    }
    return options;
  };

  const handleMonthSelect = (e) => {
    const { value } = e.target;
    const [month, year] = value.split("-").map(Number);
    const newDate = DateTime.fromObject({ month, year, day: 1 });
    setCurrentWeek(newDate.startOf("week"));
    setActiveDay(newDate);
    setSelectedMonth(month);
    setSelectedYear(year);
  };

  useEffect(() => {
    setSelectedMonth(activeDay.month);
    setSelectedYear(activeDay.year);
  }, [activeDay]);

  return (
    <div className="card-base card-base-background card-base-border flex flex-col items-center w-full relative">
      <button
        onClick={goToToday}
        className="absolute top-0 left-1/2 -translate-x-1/2 bg-white card-base-border border-t-0 rounded-b-md px-2 py-1 text-s hover:shadow-sm cursor-pointer transition-all duration-200"
      >
        Today
      </button>

      <div className="flex items-center justify-between w-full px-4 pt-4 pb-6 !mt-8">
        <button
          onClick={goToPreviousWeek}
          aria-label="Previous week"
          className="bg-white card-base-border rounded-full p-2 hover:shadow-sm cursor-pointer transition-all duration-200"
        >
          <TbArrowLeft />
        </button>
        <div className="flex items-center gap-1">
          <select
            value={`${selectedMonth}-${selectedYear}`}
            onChange={handleMonthSelect}
            className="text-lg font-semibold bg-transparent border-none focus:outline-none"
          >
            {getMonthOptions().map(({ month, year, label }) => (
              <option key={`${month}-${year}`} value={`${month}-${year}`}>
                {label}
              </option>
            ))}
          </select>
        </div>
        <button
          onClick={goToNextWeek}
          aria-label="Next week"
          className="bg-white card-base-border rounded-full p-2 hover:shadow-sm cursor-pointer transition-all duration-200"
        >
          <TbArrowRight />
        </button>
      </div>
      <div className="flex flex-row justify-between w-full px-4 pb-4">
        {daysOfWeek.map((day) => {
          const isActive = activeDay?.toISODate() === day.toISODate();
          const isToday = today.toISODate() === day.toISODate();
          const dayAbbreviation = day.toFormat('ccc').charAt(0);
          const hasEntry = hasJournalEntry(day);
          return (
            <button
              key={day.toISODate()}
              onClick={() => setActiveDay(day)}
              className={classnames(
                "flex-1 flex flex-col items-center justify-center gap-2",
                "focus:outline-none"
              )}
              aria-label={`Select ${day.toLocaleString(DateTime.DATE_MED)}`}
            >
              <span
                className={classnames(
                  "w-9 h-14 flex flex-col items-center justify-center rounded-full text-base transition-all mx-auto",
                  {
                    "border-2 border-primary bg-transparent text-primary hover:bg-blue-50":
                      isActive && !isToday,
                    "border-2 border-primary bg-blue-100 text-primary hover:bg-blue-200":
                      isActive && isToday,
                    "bg-white hover:bg-blue-50": !isActive && !isToday,
                    "text-primary bg-blue-100 hover:bg-blue-200": isToday && !isActive,
                  }
                )}
              >
                <span className="text-xs leading-none py-1 mt-2">{dayAbbreviation}</span>
                <span className="text-base">{day.day}</span>
              </span>
              <div
                className={classnames(
                  "w-8 h-8 rounded-full flex items-center justify-center transition-all",
                  {
                    "bg-blue-500 border border-blue-500 text-white": hasEntry,
                    "border border-gray-300 text-gray-300": !hasEntry,
                  }
                )}
              >
                {hasEntry ? <GiNotebook size={16} /> : <MdOutlineEdit size={16} />}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

MobileCalendar.propTypes = {
  activeDay: PropTypes.instanceOf(DateTime).isRequired,
  setActiveDay: PropTypes.func.isRequired,
  userData: PropTypes.object,
};

export default MobileCalendar;
